import { useState } from 'react'

import { useDownloadFeatureListExcel } from '@/common/api/modules/featureList'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

type FeatureItem = {
  category: string
  group: string
  menu: string
  path: string
  note?: string
  features: string[]
}

const FEATURE_DATA: FeatureItem[] = [
  // ============================================================
  // 全体管理者
  // ============================================================
  {
    category: '全体管理者',
    group: '全体管理',
    menu: 'ダッシュボード',
    path: '/dashboard',
    features: [
      '全体サマリー（登録サイト数・有償化サイト数・30日アクティブサイト数・ユーザー数・商品数）',
      'サービス別利用状況カード（連動データ/Reco-Media/Media-DRIVER/GEO-LLMO/UGC Intelligence/Findee/カルテ/AIペルソナ/メールレコメンド）：採用率・30日アクティブ・業務量・前月比・事業効果',
      '直近30日 サービス別業務量トレンド（折れ線）',
      '要対応アラート（FTP連携停止・配信ゼロ・投稿ゼロ等の異常検知、重要度別）',
      'イベント種別チャート（view/cart/purchase分布）',
      'アルゴリズム性能比較（CTR）',
      'モデル再学習（全サイト一括）'
    ]
  },
  {
    category: '全体管理者',
    group: '全体管理',
    menu: 'サイト管理',
    path: '/sites',
    features: [
      'サイト一覧・新規作成・編集・削除',
      'サイトセレクタによる各サイト画面への切替',
      'FTP接続情報管理（有効化・ユーザー名・パスワード生成・接続情報コピー）',
      'サイトアカウント（サイトオーナー）発行・権限表示',
      'テスト/本番フラグ管理',
      '機能フラグ管理（Reco-Media / メールレコメンド / Media-DRIVER / GEO-LLMO / UGC Intelligence / Findee / 検索サジェスト / カルテ / 記事アップロード / 全行動取得）'
    ]
  },
  {
    category: '全体管理者',
    group: '全体管理',
    menu: 'オペレーター管理',
    path: '/operators',
    features: [
      'W2全体オペレーター一覧・新規作成・編集・削除',
      '権限選択（管理者 / サイト管理者 / 一般）',
      'サイト割り当て（非管理者は必須）',
      'パスワード設定（8文字以上、bcryptハッシュ化）'
    ]
  },
  {
    category: '全体管理者',
    group: '全体管理',
    menu: '監査ログ',
    path: '/audit-logs',
    features: ['システム全体の操作ログ表示', '検索・フィルタ', '操作者・対象・日時の追跡']
  },
  {
    category: '全体管理者',
    group: '全体管理',
    menu: 'システム設定',
    path: '/system-config',
    features: ['SMTP設定（メール通知用）', 'FTPサーバーIP設定', 'グローバル設定値管理']
  },
  {
    category: '全体管理者',
    group: '顧客契約管理',
    menu: 'サイト状況',
    path: '/site-usage',
    features: [
      '各サイトの利用状況サマリー',
      'API呼び出し数・データ件数・機能利用フラグの一覧',
      'ヘルススコア表示'
    ]
  },
  {
    category: '全体管理者',
    group: '顧客契約管理',
    menu: '請求管理',
    path: '/billing',
    features: [
      '請求一覧タブ：サイト別月別請求の作成・編集・ステータス管理（draft/confirmed/invoiced/paid）',
      'サービス利用状況タブ：月選択式でサイト×サービスの月次利用量を表示',
      'Reco-Media は「タグデータ送信数」と「レコメンド配信数」の2指標',
      'Media-DRIVER は「記事生成数」と「アップロード数」の2指標',
      '連動データ/GEO-LLMO/UGC Intelligence/Findee/カルテ の各利用量'
    ]
  },
  {
    category: '全体管理者',
    group: '営業資料',
    menu: 'サービス紹介資料',
    path: '/proposal, /proposal2, /proposal3',
    features: [
      'Reco-Media（AIレコメンド）提案資料',
      'Media-DRIVER（コンテンツ生成）提案資料',
      'Findee（AI購買支援）提案資料',
      'スライドプレゼンテーション表示'
    ]
  },
  {
    category: '全体管理者',
    group: '社内サポート',
    menu: 'サポート仕様',
    path: '/support-spec',
    features: ['全メニューの仕様ドキュメント', '機能説明・FAQ', 'グループ別検索・フィルタ']
  },
  {
    category: '全体管理者',
    group: '社内サポート',
    menu: '機能一覧',
    path: '/feature-list',
    features: [
      '全メニュー・機能の一覧表示',
      '大分類・中分類フィルタ、キーワード検索',
      'Excelダウンロード'
    ]
  },

  // ============================================================
  // 各事業者 - サイト設定
  // ============================================================
  {
    category: '各事業者',
    group: 'サイト設定',
    menu: 'オペレーター管理',
    path: '/sites/:siteId/site-operators',
    note: 'サイト管理者以上',
    features: [
      'サイト内オペレーター一覧・作成・編集・削除',
      'ロール設定（サイト管理者 / 一般）',
      '権限テンプレート割り当て'
    ]
  },
  {
    category: '各事業者',
    group: 'サイト設定',
    menu: '権限管理',
    path: '/sites/:siteId/permissions',
    note: 'サイト管理者以上',
    features: [
      'メニュー単位の権限テンプレート作成・編集',
      'サイトが契約している機能のみ表示',
      '記事承認権限の付与'
    ]
  },

  // ============================================================
  // 各事業者 - データ基盤（W2マスタ）
  // ============================================================
  {
    category: '各事業者',
    group: 'データ基盤',
    menu: 'マスタデータ状況',
    path: '/sites/:siteId/master-summary',
    features: [
      '登録済みマスタ件数のサマリー（ユーザー・商品・受注・コンテンツ等）',
      '60秒キャッシュによる高速表示'
    ]
  },
  {
    category: '各事業者',
    group: 'データ基盤',
    menu: 'サイトダッシュボード',
    path: '/sites/:siteId/recommend/dashboard',
    features: [
      '商品・カテゴリ・紐づけ・在庫・受注・顧客・アカウント数カード',
      '月別行動推移グラフ（前月比較）',
      '月別受注推移グラフ（前月比較）',
      'レコメンド成果（クリック・CV・売上・CVR）',
      '月ナビゲーション',
      '配信サンプリングレート調整'
    ]
  },
  {
    category: '各事業者',
    group: 'データ基盤',
    menu: '商品マスタ / カテゴリ / 紐づけ / 在庫',
    path: '/sites/:siteId/products, /categories, /item-category, /inventory',
    note: 'Basicモードのみ',
    features: [
      '商品一覧・登録・編集・削除',
      'カテゴリ階層管理（最大9階層）・インライン編集',
      '商品-カテゴリ紐づけ（複数カテゴリ対応）',
      '在庫数登録・更新・ステータスカラー表示',
      'CSV一括アップロード'
    ]
  },
  {
    category: '各事業者',
    group: 'データ基盤',
    menu: 'W2マスタ管理（商品系）',
    path: '/sites/:siteId/w2-products ほか',
    note: 'W2 Fullモードのみ',
    features: [
      '商品マスタ / 商品リンク / 商品追加情報 / 商品バリエーション追加情報 / ブランド / カテゴリ / タグ / 商品グループ / グループ明細 / 在庫',
      '商品追加情報・商品バリエーション追加情報はW2の全件連携を全件置き換えで取込（ファイルに無い行を削除。削除件数は取込実行ログに表示）',
      '一覧・検索・ソート・ページング',
      '詳細パネル表示・新規登録・編集・削除',
      'CSV一括アップロード（500件バッチ・プログレス表示）'
    ]
  },
  {
    category: '各事業者',
    group: 'データ基盤',
    menu: 'W2マスタ管理（ユーザー系）',
    path: '/sites/:siteId/w2-users ほか',
    note: 'W2 Fullモードのみ',
    features: ['ユーザーマスタ / ユーザー属性 / アクセスユーザーログ', 'CSV一括アップロード']
  },
  {
    category: '各事業者',
    group: 'データ基盤',
    menu: 'W2マスタ管理（受注系）',
    path: '/sites/:siteId/w2-orders ほか',
    note: 'W2 Fullモードのみ',
    features: [
      '受注マスタ / 受注明細 / カート投入(未注文) / クーポン利用状況 / クーポンマスタ',
      'CSV一括アップロード'
    ]
  },
  {
    category: '各事業者',
    group: 'データ基盤',
    menu: 'W2マスタ管理（コンテンツ系）',
    path: '/sites/:siteId/w2-features ほか',
    note: 'W2 Fullモードのみ',
    features: [
      '広告コードマスタ / 特集ページ / 特集ページカテゴリ / 特集ページコンテンツ',
      'CSV一括アップロード'
    ]
  },
  {
    category: '各事業者',
    group: 'データ基盤',
    menu: 'ファイル取込設定',
    path: '/sites/:siteId/file-upload',
    features: [
      '手動CSVアップロード（プログレスバー付き）',
      'CSVフォーマット仕様表示（Basic/W2 Full切替）',
      '自動実行ログ・FTP連携ログ（30件ページング）',
      'FTP自動取込バッジ・リレーサーバーステータス',
      'リトライ機能・メール通知設定'
    ]
  },

  // ============================================================
  // 各事業者 - Reco-Media（AIレコメンド）
  // ============================================================
  {
    category: '各事業者',
    group: 'Reco-Media',
    menu: 'ダッシュボード',
    path: '/sites/:siteId/recommend/dashboard',
    note: 'enable_reco_media',
    features: ['配信数・クリック数・CV数・CVR等のKPI表示', 'レコメンド成果サマリー']
  },
  {
    category: '各事業者',
    group: 'Reco-Media',
    menu: '配信履歴 / 分析',
    path: '/sites/:siteId/recommend/dashboard/delivery-stats',
    note: 'enable_reco_media',
    features: [
      'KPI表示（配信数・クリック数・CV数・売上）',
      '日別推移チャート',
      'ウィジェット別実績',
      'A/Bテスト内訳'
    ]
  },
  {
    category: '各事業者',
    group: 'Reco-Media',
    menu: '訪問者履歴 / 行動履歴 / 受注履歴',
    path: '/sites/:siteId/recommend/customers, /recommend/behavior, /recommend/order-history',
    note: 'enable_reco_media',
    features: [
      '訪問者一覧（ステータス・デバイス・配信数・各アクション数）・行動分析モーダル・AIインサイト',
      '行動ログ一覧（タグ取得）・ページランキング分析（日/週/月別）・AI分析',
      '受注一覧（Basic: OrderHistory / W2 Full: w2_Order+明細）・商品ランキング・AI分析・CSV取込'
    ]
  },
  {
    category: '各事業者',
    group: 'Reco-Media',
    menu: 'レコメンドパーツ管理（ウィジェット）',
    path: '/sites/:siteId/recommend/widgets',
    note: 'enable_reco_media',
    features: [
      'レコメンドパーツ作成・編集・削除',
      '表示ロジック（BB/OB/Personal/Similar/Adaptive/Ranking Blend/Recently Viewed/Mail）',
      'レイアウト設定（Grid/Carousel/List）・表示数・タイトル・カスタムCSS',
      'A/Bテスト設定（バリアント数・トラフィック配分）',
      'ビジュアルCSSエディタ（AI生成対応）・ライブプレビュー'
    ]
  },
  {
    category: '各事業者',
    group: 'Reco-Media',
    menu: 'タグ設置ガイド',
    path: '/sites/:siteId/recommend/tags',
    note: 'enable_reco_media',
    features: [
      '基本タグ / 商品閲覧タグ / カートタグ / 購入完了タグ / メール用レコメンドタグ生成（メール用は enable_mail_reco のサイトのみ）',
      '顧客カルテオプション（フル行動取得）切替',
      'タグ埋め込みガイド'
    ]
  },
  {
    category: '各事業者',
    group: 'Reco-Media',
    menu: 'A/Bテスト管理',
    path: '/sites/:siteId/ab-campaigns',
    note: 'enable_reco_media',
    features: [
      'A/Bテストキャンペーン作成・実行・停止',
      '目標KPI設定（CTR / CVR / 収益）',
      '統計的有意差検定（2標本比率z検定）・累積推移グラフ・勝者自動判定'
    ]
  },
  {
    category: '各事業者',
    group: 'Reco-Media',
    menu: 'AIモデルチューニング',
    path: '/sites/:siteId/recommend/model-tuning',
    note: 'enable_reco_media',
    features: [
      'ハイブリッド重み調整（Collaborative / Neural / Content-Based）',
      '各エンジンのハイパーパラメータ調整',
      'モデル再学習実行（バックグラウンド・プログレス）・再学習履歴'
    ]
  },
  {
    category: '各事業者',
    group: 'Reco-Media',
    menu: 'AIタグ分析',
    path: '/sites/:siteId/recommend/ai-tags',
    note: 'enable_reco_media',
    features: ['AI自動抽出タグ一覧', '手動タグマッピング', '効果トラッキング（CTR・CVR寄与）']
  },
  {
    category: '各事業者',
    group: 'Reco-Media',
    menu: '初期設定ガイド',
    path: '/sites/:siteId/recommend/setup-guide',
    note: 'enable_reco_media',
    features: [
      '導入進捗チェックリスト（5フェーズ）',
      '進捗率バー',
      '各ステップ完了確認・クイックリンク'
    ]
  },

  // ============================================================
  // 各事業者 - Media-DRIVER（コンテンツ生成）
  // ============================================================
  {
    category: '各事業者',
    group: 'Media-DRIVER',
    menu: 'ダッシュボード',
    path: '/sites/:siteId/media/dashboard',
    note: 'enable_content_analysis',
    features: ['コンテンツ生成状況サマリー', '市場・競合・自社シグナル表示']
  },
  {
    category: '各事業者',
    group: 'Media-DRIVER',
    menu: 'サイト現状分析',
    path: '/sites/:siteId/media/analysis',
    note: 'enable_content_analysis',
    features: ['自サイトドメイン分析', '競合サイト分析', 'Favドメイン/Favブランド登録']
  },
  {
    category: '各事業者',
    group: 'Media-DRIVER',
    menu: '特集記事作成',
    path: '/sites/:siteId/article-creator',
    note: 'enable_content_analysis',
    features: [
      'AI記事生成（タイトル・メタディスクリプション・本文）',
      'キーワード→構成案→本文のワンストップ生成',
      'E-E-A-T準拠チェック・品質スコア'
    ]
  },
  {
    category: '各事業者',
    group: 'Media-DRIVER',
    menu: '特集リライト判定',
    path: '/sites/:siteId/feature-rewrite',
    note: 'enable_content_analysis',
    features: ['SEOスコア計算（5項目100点）', '改善提案AI生成', '公開/非公開状態管理']
  },
  {
    category: '各事業者',
    group: 'Media-DRIVER',
    menu: 'アップロード管理',
    path: '/sites/:siteId/article-uploads',
    note: 'enable_content_analysis（記事アップロード）',
    features: [
      '作成記事のアップロード申請・状況管理',
      '承認フロー（投票）',
      'W2特集ページへのAPI連携アップロード',
      '公開スケジュール管理'
    ]
  },
  {
    category: '各事業者',
    group: 'Media-DRIVER',
    menu: '推奨アクション管理',
    path: '/sites/:siteId/media/action',
    note: 'enable_content_analysis',
    features: ['推奨アクションの承認ワークフロー', 'ロードマップ（スケジュール管理）']
  },
  {
    category: '各事業者',
    group: 'Media-DRIVER',
    menu: '分析の自動実行',
    path: '/sites/:siteId/analysis-schedules',
    note: 'enable_content_analysis',
    features: ['定期分析・レポート生成のスケジュール管理', '実行履歴']
  },

  // ============================================================
  // 各事業者 - GEO/LLMO（AI検索最適化）
  // ============================================================
  {
    category: '各事業者',
    group: 'GEO/LLMO',
    menu: 'サイテーション分析',
    path: '/sites/:siteId/citation-analysis',
    note: 'enable_geo_llmo',
    features: [
      '自社ブランド・競合ブランド入力',
      'Favドメイン/Favブランド登録',
      'サイテーション指標分析（ブランド登場数・カテゴリ別）',
      '競合SERP比較'
    ]
  },
  {
    category: '各事業者',
    group: 'GEO/LLMO',
    menu: 'シェア・オブ・ボイス分析',
    path: '/sites/:siteId/share-of-voice',
    note: 'enable_geo_llmo',
    features: ['複数競合との比較', 'カテゴリ別シェア率（円グラフ）', 'ブランド別スコア比較']
  },
  {
    category: '各事業者',
    group: 'GEO/LLMO',
    menu: 'センチメント分析',
    path: '/sites/:siteId/sentiment-analysis',
    note: 'enable_geo_llmo',
    features: ['ポジティブ/ニュートラル/ネガティブ分布', '複数競合との比較', '時系列推移']
  },
  {
    category: '各事業者',
    group: 'GEO/LLMO',
    menu: 'クエリ・ファンアウト分析',
    path: '/sites/:siteId/query-fanout',
    note: 'enable_geo_llmo',
    features: [
      'キーワード分析（CVEスコア・検索ボリューム・難易度）',
      'スキャッタープロット',
      '子記事モーダル',
      'AI概要生成'
    ]
  },

  // ============================================================
  // 各事業者 - UGC Intelligence
  // ============================================================
  {
    category: '各事業者',
    group: 'UGC Intelligence',
    menu: 'ダッシュボード',
    path: '/sites/:siteId/ugc-dashboard',
    note: 'enable_review',
    features: ['UGC関連の総合KPI表示', '投稿数・掲載数・評価サマリー']
  },
  {
    category: '各事業者',
    group: 'UGC Intelligence',
    menu: 'レビュー依頼配信設定',
    path: '/sites/:siteId/ugc-mail',
    note: 'enable_review',
    features: [
      '購入者へのレビュー募集メール/SNS依頼の設定・配信',
      'フォローアップルール',
      '配信スケジュール'
    ]
  },
  {
    category: '各事業者',
    group: 'UGC Intelligence',
    menu: 'レビュー管理',
    path: '/sites/:siteId/ugc-reviews',
    note: 'enable_review',
    features: [
      '寄せられたレビュー・評価の審査・掲載・削除',
      'レビューフォーム項目設定',
      'レビューテンプレート'
    ]
  },
  {
    category: '各事業者',
    group: 'UGC Intelligence',
    menu: 'ギフティング',
    path: '/sites/:siteId/ugc-gifting',
    note: 'enable_review',
    features: [
      'UGC作成者へのインセンティブ配布',
      'キャンペーン管理・応募者管理',
      '当選者選定・発送CSV・発送済み記録・リマインド'
    ]
  },
  {
    category: '各事業者',
    group: 'UGC Intelligence',
    menu: 'Instagram UGC',
    path: '/sites/:siteId/ugc-instagram',
    note: 'enable_review',
    features: ['Instagram からの UGC 自動収集・キュレーション']
  },
  {
    category: '各事業者',
    group: 'UGC Intelligence',
    menu: 'マルチCH配信',
    path: '/sites/:siteId/ugc-distribution',
    note: 'enable_review',
    features: ['UGC をメール・LINE・プッシュ等の複数チャネルに配信']
  },
  {
    category: '各事業者',
    group: 'UGC Intelligence',
    menu: 'スタッフ投稿',
    path: '/sites/:siteId/ugc-staff',
    note: 'enable_review',
    features: ['店舗スタッフ等による公式投稿管理']
  },
  {
    category: '各事業者',
    group: 'UGC Intelligence',
    menu: 'Q&A管理 / 動画コマース',
    path: '/sites/:siteId/ugc-qa, /ugc-video',
    note: 'enable_review',
    features: ['ユーザーQ&A・FAQ管理', '動画UGC編集・ショッパブルビデオ']
  },
  {
    category: '各事業者',
    group: 'UGC Intelligence',
    menu: 'ロイヤルティ',
    path: '/sites/:siteId/ugc-loyalty',
    note: 'enable_review',
    features: ['UGC投稿者への会員ランク・ポイント付与', 'ロイヤルティ設定・ポイント履歴']
  },
  {
    category: '各事業者',
    group: 'UGC Intelligence',
    menu: 'GDPR/DSA（コンプライアンス）',
    path: '/sites/:siteId/ugc-compliance',
    note: 'enable_review',
    features: ['EU規制対応（同意管理・データ削除要求）', '同意ログ管理']
  },
  {
    category: '各事業者',
    group: 'UGC Intelligence',
    menu: 'ポータル管理 / 通知設定',
    path: '/sites/:siteId/ugc-portal, /ugc-notifications',
    note: 'enable_review',
    features: ['UGCポータルサイトの設定', 'メール・Slack・Webhook通知ルール']
  },
  {
    category: '各事業者',
    group: 'UGC Intelligence',
    menu: '効果・分析（CVR/SEO/KPI/A-B/VOC/ベンチマーク/鮮度）',
    path: '/sites/:siteId/ugc-cvr ほか',
    note: 'enable_review',
    features: [
      'CVRダッシュボード（UGC経由のCVR・売上）',
      'SEOダッシュボード（UGCによるSEO効果）',
      '媒体別KPIレポート',
      'A/Bテスト管理',
      'VOC/NPS分析',
      'ベンチマーク比較',
      'コンテンツ鮮度アラート'
    ]
  },
  {
    category: '各事業者',
    group: 'UGC Intelligence',
    menu: 'AIスコアリング設定',
    path: '/sites/:siteId/ugc-ai-score',
    note: 'enable_review',
    features: ['UGC品質の自動採点・フィルタリング']
  },
  {
    category: '各事業者',
    group: 'UGC Intelligence',
    menu: '配信タグ・表示設定',
    path: '/sites/:siteId/ugc-tag-embed ほか',
    note: 'enable_review',
    features: ['配信タグ設定（埋め込み・トラッキング）', 'UGC 商品タグ付与', 'サイト表示枠設定']
  },
  {
    category: '各事業者',
    group: 'UGC Intelligence',
    menu: 'Instagram設定',
    path: '/sites/:siteId/ugc-instagram-settings',
    note: 'enable_review',
    features: [
      'Instagram Business Account の接続（トークン検証つき）',
      '監視ハッシュタグ・自動収集の頻度',
      'ブランド情報（ブランド名・ロゴ・デフォルト許諾メッセージ）'
    ]
  },
  {
    category: '各事業者',
    group: 'UGC Intelligence',
    menu: '設定ガイド',
    path: '/sites/:siteId/ugc-guide',
    note: 'enable_review',
    features: ['UGC Intelligence のセットアップガイド・チュートリアル']
  },

  // ============================================================
  // 各事業者 - Findee（AI購買支援）
  // ============================================================
  {
    category: '各事業者',
    group: 'Findee',
    menu: 'ダッシュボード',
    path: '/sites/:siteId/ai-purchase-assist',
    note: 'enable_ai_purchase_assist',
    features: ['会話数・経由CVR・GMV・LLMコスト・推薦商品ランキング', '経由 vs 未経由比較']
  },
  {
    category: '各事業者',
    group: 'Findee',
    menu: '配信設定',
    path: '/sites/:siteId/ai-purchase-assist/campaigns',
    note: 'enable_ai_purchase_assist',
    features: [
      'チャット配信キャンペーン・対象ページ・起動条件・デザイン・AI動作設定',
      '実環境同様の対話テスト'
    ]
  },
  {
    category: '各事業者',
    group: 'Findee',
    menu: 'リッチUIデザイナー',
    path: '/sites/:siteId/ai-purchase-assist/rich-ui',
    note: 'enable_ai_purchase_assist',
    features: ['会話文脈に応じた10種リッチUIの編集', 'UI・会話フローのビジュアル編集']
  },
  {
    category: '各事業者',
    group: 'Findee',
    menu: 'A/Bテスト',
    path: '/sites/:siteId/ai-purchase-assist/ab-tests',
    note: 'enable_ai_purchase_assist',
    features: [
      '応答パターンの効果比較',
      "カイ二乗検定 + Cohen's h + 早期停止判定",
      'ON/OFF導入効果証明'
    ]
  },
  {
    category: '各事業者',
    group: 'Findee',
    menu: '会話履歴・分析',
    path: '/sites/:siteId/ai-purchase-assist/conversations',
    note: 'enable_ai_purchase_assist',
    features: ['過去の会話ログ・ユーザー分析', '購買連動（24時間以内購入・推薦クリック経由）']
  },
  {
    category: '各事業者',
    group: 'Findee',
    menu: 'フィードバック管理 / ナレッジ',
    path: '/sites/:siteId/ai-purchase-assist/feedback, /knowledge',
    note: 'enable_ai_purchase_assist',
    features: ['ユーザーフィードバック・改善提案', 'FAQ・商品情報・回答テンプレート管理']
  },
  {
    category: '各事業者',
    group: 'Findee',
    menu: 'AIプロファイル / NGルール',
    path: '/sites/:siteId/ai-purchase-assist/ai-profiles, /ng-rules',
    note: 'enable_ai_purchase_assist',
    features: ['ボットの人格・トーン・性格設定', 'NGワード・禁止トピック設定・検知ログ']
  },
  {
    category: '各事業者',
    group: 'Findee',
    menu: 'ベクトル化データ管理',
    path: '/sites/:siteId/ai-purchase-assist/vector-sources',
    note: 'enable_ai_purchase_assist',
    features: [
      '埋め込みベクトル・RAGデータソース管理',
      '商品マスタのベクトル化：表示対象の全商品が対象。商品名・読み・ブランド・カテゴリ・スペック（商品追加情報）・タグ・バリエーション・価格に商品意味理解の抽出結果を重ねた本文。本文が変わった商品だけ埋め込み直し、表示対象外になった商品のベクトルは削除',
      'UGCレビュー本文のベクトル化（過去レビューからAI回答）'
    ]
  },

  // ============================================================
  // 各事業者 - 検索サジェスト（Findee）
  // ============================================================
  {
    category: '各事業者',
    group: '検索サジェスト',
    menu: '検索サジェスト設定',
    path: '/sites/:siteId/search-suggest/settings',
    note: 'enable_search_suggest',
    features: [
      'サジェスト全体・種別（ワード/商品/記事）の有効化',
      '表示件数・並び順・入力前の人気キーワード表示',
      '表記ゆれ正規化・誤字補正（しきい値・補正モード）',
      'パーソナライズ（受注履歴の重み・レコメンド連携）'
    ]
  },
  {
    category: '各事業者',
    group: '検索サジェスト',
    menu: '配信設定',
    path: '/sites/:siteId/search-suggest/delivery',
    note: 'enable_search_suggest',
    features: [
      '埋め込みタグ発行・検索窓セレクタ指定・疎通確認',
      '配信面の出し分け（PC/SP・ページ種別）',
      'デザイン（テーマカラー・サムネ/価格表示）',
      '公開管理（下書き/公開/バージョン/ロールバック）'
    ]
  },
  {
    category: '各事業者',
    group: '検索サジェスト',
    menu: '辞書管理',
    path: '/sites/:siteId/search-suggest/dictionary',
    note: 'enable_search_suggest',
    features: [
      'マスター連携（Sudachiで辞書ビルド・増分更新）',
      'シノニム・類似語辞書',
      '補正辞書 承認キュー',
      '手動キーワード（ピン留め・誘導先・掲載期間）',
      '0件キーワード一覧（辞書改善導線）'
    ]
  },
  {
    category: '各事業者',
    group: '検索サジェスト',
    menu: 'ダッシュボード',
    path: '/sites/:siteId/search-suggest/analytics',
    note: 'enable_search_suggest',
    features: [
      'KPI（利用率・経由CTR・経由CVR・0件ヒット率）',
      '推移グラフ・人気サジェスト語ランキング',
      '0件・未ヒット分析（辞書管理への導線）'
    ]
  }
]

const GROUP_BADGE: Record<string, string> = {
  全体管理: 'bg-surface-critical text-critical border border-red-200',
  顧客契約管理: 'bg-surface-critical text-critical border border-red-200',
  営業資料: 'bg-surface-critical text-critical border border-red-200',
  社内サポート: 'bg-surface-critical text-critical border border-red-200',
  サイト設定: 'bg-surface-secondary text-secondary border border-gray-200',
  データ基盤: 'bg-emphasis text-emphasis border border-blue-100',
  'Reco-Media': 'bg-assist text-assist border border-purple-200',
  'Media-DRIVER': 'bg-success text-success border border-green-200',
  'GEO/LLMO': 'bg-surface-warning text-warning border border-orange-200',
  'UGC Intelligence': 'bg-surface-critical text-critical border border-pink-200',
  Findee: 'bg-assist text-assist border border-indigo-200',
  検索サジェスト: 'bg-surface-warning text-warning border border-orange-200',
  カルテ: 'bg-emphasis text-emphasis border border-sky-200'
}

const CATEGORY_BADGE: Record<string, string> = {
  全体管理者: 'bg-surface-critical text-critical',
  各事業者: 'bg-assist text-assist'
}

const FeatureListPage = () => {
  const [filterCategory, setFilterCategory] = useState('')
  const [filterGroup, setFilterGroup] = useState('')
  const [searchText, setSearchText] = useState('')

  const { trigger: downloadExcel } = useDownloadFeatureListExcel((blob) => {
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'W2SmartHub_機能一覧.xlsx'
    a.click()
  })

  const categories = [...new Set(FEATURE_DATA.map((d) => d.category))]
  const groups = [...new Set(FEATURE_DATA.map((d) => d.group))]

  const filtered = FEATURE_DATA.filter((item) => {
    if (filterCategory && item.category !== filterCategory) return false
    if (filterGroup && item.group !== filterGroup) return false
    if (searchText) {
      const q = searchText.toLowerCase()
      return (
        item.menu.toLowerCase().includes(q) ||
        item.features.some((f) => f.toLowerCase().includes(q)) ||
        (item.note ?? '').toLowerCase().includes(q)
      )
    }
    return true
  })

  const grouped = filtered.reduce<
    Record<string, { category: string; group: string; items: FeatureItem[] }>
  >((acc, item) => {
    const key = `${item.category}__${item.group}`
    if (!acc[key]) acc[key] = { category: item.category, group: item.group, items: [] }
    acc[key].items.push(item)
    return acc
  }, {})

  const totalFeatures = FEATURE_DATA.reduce((s, d) => s + d.features.length, 0)

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='機能一覧'
        description={`W2 Smart Hub の全メニュー・機能を一覧で確認できます（${FEATURE_DATA.length} メニュー / ${totalFeatures} 機能）`}
        rightNode={
          <Button color={ButtonColor.SECONDARY} size='large' onClick={() => downloadExcel({})}>
            Excelダウンロード
          </Button>
        }
      />

      {/* フィルター */}
      <div className='mb-5 flex flex-wrap items-center gap-2'>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className='border-secondary bg-surface text-primary focus:border-focus rounded-lg border px-3 py-2 text-xs outline-none'
        >
          <option value=''>大分類: すべて</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={filterGroup}
          onChange={(e) => setFilterGroup(e.target.value)}
          className='border-secondary bg-surface text-primary focus:border-focus rounded-lg border px-3 py-2 text-xs outline-none'
        >
          <option value=''>中分類: すべて</option>
          {groups.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
        <input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder='キーワード検索...'
          className='border-secondary bg-surface text-primary placeholder:text-disabled focus:border-focus min-w-44 flex-1 rounded-lg border px-3 py-2 text-xs outline-none'
        />
        {(filterCategory || filterGroup || searchText) && (
          <button
            onClick={() => {
              setFilterCategory('')
              setFilterGroup('')
              setSearchText('')
            }}
            className='border-caution bg-surface-caution text-caution rounded-lg border px-3.5 py-2 text-xs font-semibold'
          >
            クリア
          </button>
        )}
        <span className='text-secondary ml-auto text-xs'>
          {filtered.length} / {FEATURE_DATA.length} メニュー表示中
        </span>
      </div>

      {/* 一覧 */}
      {Object.values(grouped).map(({ category, group, items }) => (
        <div key={`${category}__${group}`} className='mb-6'>
          {/* グループヘッダー */}
          <div className='mb-3 flex items-center gap-2'>
            <span
              className={`rounded-md px-3 py-0.5 text-xs font-bold ${CATEGORY_BADGE[category] ?? 'bg-surface-secondary text-secondary'}`}
            >
              {category}
            </span>
            <span
              className={`rounded-md px-3 py-0.5 text-xs font-bold ${GROUP_BADGE[group] ?? 'bg-surface-secondary text-secondary border-secondary border'}`}
            >
              {group}
            </span>
          </div>

          {/* メニューカード */}
          <div className='grid grid-cols-[repeat(auto-fill,minmax(340px,1fr))] gap-3'>
            {items.map((item) => (
              <div
                key={item.menu}
                className='border-secondary bg-surface shadow-area rounded-xl border px-4 py-4'
              >
                <div className='mb-1.5 flex items-center justify-between gap-2'>
                  <span className='text-primary text-sm font-bold'>{item.menu}</span>
                  {item.note && (
                    <span className='bg-surface-warning text-warning shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold'>
                      {item.note}
                    </span>
                  )}
                </div>
                <div className='text-secondary mb-2.5 font-mono text-[11px]'>{item.path}</div>
                <ul className='m-0 space-y-0.5 pl-0'>
                  {item.features.map((f, i) => (
                    <li key={i} className='text-secondary flex gap-1.5 text-xs leading-relaxed'>
                      <span className='text-tertiary mt-0.5 shrink-0'>–</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ))}

      {filtered.length === 0 && (
        <p className='text-secondary py-10 text-center text-sm'>該当する機能が見つかりません</p>
      )}
    </Main>
  )
}

export default FeatureListPage
